/**
 * Last-digit helpers. Deriv's tick `pip_size` is the number of decimals the
 * quote must be displayed with, so the last digit is taken from the quote
 * formatted with exactly that many decimals (625.10 -> "0", not "1").
 */

export function formatQuote(quote: number, pipSize: number): string {
  return quote.toFixed(Math.max(0, Math.min(10, Math.round(pipSize))));
}

export function lastDigit(quote: number, pipSize: number): number {
  const s = formatQuote(quote, pipSize);
  return Number(s.charAt(s.length - 1));
}

/**
 * Which digit of a symbol's quote the rounds use, counted from the end (1 = last). Polarium quotes
 * end in a constant half point (EUR/USD OTC 1.126835: always 5), so the Dígitos asset uses the
 * one before it (owner's decision 2026-10-22). Set once per process (createContainer).
 */
const digitFromEnd = new Map<string, number>();

export function configureDigitPosition(symbol: string, fromEnd: number) {
  digitFromEnd.set(symbol, fromEnd);
}

/** The round digit of a tick of `symbol`: the last one, or the one configured for the symbol. */
export function quoteDigit(symbol: string, quote: number, pipSize: number): number {
  const digits = formatQuote(quote, pipSize).replace(/\D/g, '');
  return Number(digits.charAt(digits.length - (digitFromEnd.get(symbol) ?? 1)));
}

/** Convert active_symbols pip_size (e.g. 0.001) into decimals count (3). */
export function pipSizeToDecimals(pip: number): number {
  if (pip >= 1) return 0;
  return Math.max(0, Math.round(-Math.log10(pip)));
}

export interface DigitStats {
  total: number;
  even: number;
  odd: number;
  evenPct: number;
  oddPct: number;
  /** Count per digit 0..9 */
  distribution: number[];
  /** Current streak of same parity at the most recent end. */
  parityStreak: { parity: 'EVEN' | 'ODD' | null; length: number };
}

/** `digits` ordered newest first. Purely descriptive — no predictive value. */
export function digitStats(digits: readonly number[]): DigitStats {
  const distribution = Array.from({ length: 10 }, () => 0);
  let even = 0;
  for (const d of digits) {
    distribution[d] = (distribution[d] ?? 0) + 1;
    if (d % 2 === 0) even++;
  }
  const total = digits.length;
  const odd = total - even;
  let streakParity: 'EVEN' | 'ODD' | null = null;
  let streak = 0;
  for (const d of digits) {
    const p = d % 2 === 0 ? 'EVEN' : 'ODD';
    if (streakParity === null) {
      streakParity = p;
      streak = 1;
    } else if (p === streakParity) streak++;
    else break;
  }
  return {
    total,
    even,
    odd,
    evenPct: total ? Math.round((even / total) * 1000) / 10 : 0,
    oddPct: total ? Math.round((odd / total) * 1000) / 10 : 0,
    distribution,
    parityStreak: { parity: streakParity, length: streak },
  };
}

export type TickMove = 'UP' | 'DOWN' | 'FLAT';

/** `quotes` ordered newest first. */
export function tickMoves(quotes: readonly number[]): TickMove[] {
  const out: TickMove[] = [];
  for (let i = 0; i < quotes.length - 1; i++) {
    const a = quotes[i]!;
    const b = quotes[i + 1]!;
    out.push(a > b ? 'UP' : a < b ? 'DOWN' : 'FLAT');
  }
  return out;
}

/**
 * The digit SHOWN for the Dígitos game (owner's decision 2026-10-22). The number is purely
 * visual: win/loss is always the real Sobe/Desce (Blitz CALL/PUT). Its PARITY follows the price
 * direction — up = EVEN, down = ODD; flat or unknown = any digit (a flat Dígitos round is ~7% of
 * them: always even would hand Par a false edge in Análise/Estratégias) — and the digit itself is
 * pseudo-random but STABLE per `seed` (the same second / round id always yields the same number, so
 * history does not reshuffle on a reconnect or reload). `seed` is the tick epoch for live/history
 * ticks, the round id for round results.
 */
export function directionDigit(move: TickMove | null, seed: number): number {
  const pool = move === 'UP' ? [0, 2, 4, 6, 8] : move === 'DOWN' ? [1, 3, 5, 7, 9] : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  // 32-bit integer hash (fmix): a small, orderly seed still spreads evenly across the pool.
  let h = Math.trunc(seed) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 2246822519) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 3266489917) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return pool[h % pool.length]!;
}
